
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Subject, pipe } from "rxjs";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ApplicationRef, ErrorHandler, Injectable, Injector, inject } from "@angular/core";

interface BroadcastEvent {
  key: any;
  data?: any;
}

@Injectable({ providedIn: "root" })
export class Broadcaster {
  private _eventBus: Subject<BroadcastEvent>;

  // Zoneless: this bus is the app's main async UI-update channel. Without
  // zone.js, subscribers that mutate view state from a broadcast won't
  // re-render on their own. One coalesced ApplicationRef.tick() per microtask
  // burst drives change detection for any number of broadcasts in the turn.
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private tickScheduled = false;

  constructor() {
    this._eventBus = new Subject<BroadcastEvent>();
  }

  broadcast(key: any, data?: any) {
    console.debug("[Broadcaster.broadcast]", key, data);
    this._eventBus.next({key, data});
    this.scheduleTick();
  }

  private scheduleTick() {
    if (this.tickScheduled) { return; }
    this.tickScheduled = true;
    queueMicrotask(() => {
      this.tickScheduled = false;
      try {
        this.appRef.tick();
      } catch (err) {
        // tick() throws if invoked re-entrantly during an in-flight CD pass;
        // the in-flight pass already covers this burst, so ignore that case
        // only. Anything else is a genuine render error and must still reach
        // the global ErrorHandler (Sentry) instead of disappearing here.
        if (!this.isRecursiveTickError(err)) {
          this.injector.get(ErrorHandler).handleError(err);
        }
      }
    });
  }

  /** NG0101: "ApplicationRef.tick is called recursively" — the only case this scheduler may ignore. */
  private isRecursiveTickError(err: any): boolean {
    const code = err && typeof err === "object" ? (err as any).code : undefined;
    if (code === -101 || code === 101) { return true; }
    const message = err && err.message ? String(err.message) : String(err);
    return message.includes("NG0101") || message.includes("tick is called recursively");
  }

  on<T>(key: any): Observable<T> {
    return this._eventBus.asObservable()
    .pipe(filter(event => event.key === key), map(event => <T>event.data));
  }
}

export class Events {
  static SESSION_EXPIRED = "SESSION_EXPIRED_BROADCAST";
  static USER_LOGGED_OUT = "USER_LOGGED_OUT";
}
